import React from "react";
import { m, LazyMotion, domAnimation } from "framer-motion";
import { ReactComponent as CloseIcon } from "../assets/icons/x.svg";
import clsx from "clsx";

type DialogProps = {
  isOpen: boolean;
  onClose: () => void;
  children: React.ReactNode;
  title?: string;
  className?: string;
  showCloseButton?: boolean;
};

export function Dialog({
  isOpen,
  onClose,
  children,
  title,
  className,
  showCloseButton = true,
}: DialogProps) {
  // Prevent body scroll when dialog is open
  React.useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "auto";
    }
    return () => {
      document.body.style.overflow = "auto";
    };
  }, [isOpen]);

  return (
    <LazyMotion features={domAnimation} strict>
      <m.div>
        {isOpen && (
          <div className="fixed inset-0 z-50 overflow-y-auto">
            <div className="flex min-h-screen items-center justify-center px-4 pt-4 pb-20 text-center sm:block sm:p-0">
              <m.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="fixed inset-0 bg-black/30 transition-opacity"
                onClick={onClose}
              />

              <span
                className="hidden sm:inline-block sm:h-screen sm:align-middle"
                aria-hidden="true"
              >
                &#8203;
              </span>

              <m.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
                className={clsx(
                  "relative inline-block transform overflow-hidden rounded-lg bg-white text-left align-bottom shadow-xl transition-all sm:my-8 sm:align-middle",
                  className
                )}
              >
                {(title || showCloseButton) && (
                  <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4">
                    {title && (
                      <h3 className="text-lg font-semibold leading-6 text-gray-900">
                        {title}
                      </h3>
                    )}
                    {showCloseButton && (
                      <button
                        onClick={onClose}
                        className="rounded-full p-1 hover:bg-gray-100"
                      >
                        <CloseIcon className="h-5 w-5 text-gray-500" />
                      </button>
                    )}
                  </div>
                )}
                <div className="px-6 py-4">{children}</div>
              </m.div>
            </div>
          </div>
        )}
      </m.div>
    </LazyMotion>
  );
}
